import React, { useState, useEffect, useRef } from 'react';
import { useScenario, useEntityFilter, useStateFilter, useFilingCodeFilter } from '../components/layout/Layout';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import { Box, Button, Typography, Paper, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Snackbar, Alert, LinearProgress, Autocomplete, IconButton } from '@mui/material';
import UnsavedChangesDialog from '../components/UnsavedChangesDialog';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import SearchableDataTable from '../components/SearchableDataTable';
import api from '../services/api';
import '../styles/EntityInputs.css';

const STATE_INPUT_COLUMNS = [
{ field: 'ScenarioCode', headerName: 'Scenario Code', minWidth: 120 },
{ field: 'FilingCode', headerName: 'Filing Code', minWidth: 150 },
{ field: 'EntityCode', headerName: 'Entity Code', minWidth: 120, flex: 1 },
{ field: 'StateCode', headerName: 'State Code', minWidth: 120 },
// Client convention: apportionment rates are entered as whole percentages and displayed without a % sign at six decimals.
{ field: 'CurrentApportionmentRate', headerName: 'Current Apportionment Rate', minWidth: 220, valueFormatter: (params) => params.value != null && params.value !== '' ? parseFloat(params.value).toFixed(6) : '' },
{ field: 'BeginningApportionmentRate', headerName: 'Begin Apportionment Rate', minWidth: 220, valueFormatter: (params) => params.value != null && params.value !== '' ? parseFloat(params.value).toFixed(6) : '' },
{ field: 'EndingApportionmentRate', headerName: 'End Apportionment Rate', minWidth: 200, valueFormatter: (params) => params.value != null && params.value !== '' ? parseFloat(params.value).toFixed(6) : '' },
{ field: 'CurrentStateRate', headerName: 'Current State Rate', minWidth: 170 },
{ field: 'BeginningStateRate', headerName: 'Begin State Rate', minWidth: 160 },
{ field: 'EndingStateRate', headerName: 'End State Rate', minWidth: 150 }];


const defaultStateInput = {
  ScenarioCode: '',
  EntityCode: '',
  StateCode: '',
  CurrentApportionmentRate: '',
  BeginningApportionmentRate: '',
  EndingApportionmentRate: '',
  CurrentStateRate: '',
  BeginningStateRate: '',
  EndingStateRate: ''
};

const RATE_FIELDS = [
'CurrentApportionmentRate',
'BeginningApportionmentRate',
'EndingApportionmentRate',
'CurrentStateRate',
'BeginningStateRate',
'EndingStateRate'];


const StateInputs = () => {
  // Export to Excel
  const exportToExcel = () => {
    try {
      const dataToExport = selectedRows.length > 0 ? rows.filter((row) => selectedRows.includes(row.id)) : rows;

      // Filter data to include only visible columns from STATE_INPUT_COLUMNS
      const filteredData = dataToExport.map((row) => {
        const filteredRow = {};
        STATE_INPUT_COLUMNS.forEach((col) => {
          filteredRow[col.headerName] = row[col.field];
        });
        return filteredRow;
      });

      // Always include headers even if no data by specifying header option
      const headers = STATE_INPUT_COLUMNS.map((col) => col.headerName);
      const ws = XLSX.utils.json_to_sheet(filteredData, { header: headers });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'StateInputs');
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], { type: 'application/octet-stream' });
      saveAs(blob, 'state_inputs.xlsx');
      setNotification({ open: true, message: 'Export successful', severity: 'success' });
    } catch (error) {
      setNotification({ open: true, message: 'Failed to export state inputs', severity: 'error' });
    }
  };

  const { selectedScenario } = useScenario();
  const { selectedEntity } = useEntityFilter();
  const { selectedState } = useStateFilter();
  const { selectedFilingCode } = useFilingCodeFilter();

  const [allRows, setAllRows] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentRow, setCurrentRow] = useState(defaultStateInput);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const [selectedRows, setSelectedRows] = useState([]);
  const [masterEntities, setMasterEntities] = useState([]);
  const [masterStates, setMasterStates] = useState([]);
  const [entityLoading, setEntityLoading] = useState(false);
  const [stateLoading, setStateLoading] = useState(false);

  // Import state
  const fileInputRef = useRef(null);
  const [importFile, setImportFile] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importStats, setImportStats] = useState({ total: 0, updated: 0, failed: 0, skipped: 0, notFound: 0 });

  // Fetch master entities
  const fetchMasterEntities = async () => {
    setEntityLoading(true);
    try {
      const response = await api.get('/entity-models/master');
      if (response.data && response.data.data) {
        setMasterEntities(response.data.data);
      }
    } catch (error) {
      setNotification({ open: true, message: 'Failed to load master entities', severity: 'error' });
    } finally {
      setEntityLoading(false);
    }
  };

  // Fetch master states
  const fetchMasterStates = async () => {
    setStateLoading(true);
    try {
      const response = await api.get('/states/master');
      if (response.data && response.data.data) {
        setMasterStates(response.data.data);
      }
    } catch (error) {
      setNotification({ open: true, message: 'Failed to load master states', severity: 'error' });
    } finally {
      setStateLoading(false);
    }
  };

  useEffect(() => {
    fetchMasterEntities();
    fetchMasterStates();
  }, []);

  // Fetch all state inputs
  const fetchStateInputs = async () => {
    setLoading(true);
    try {
      const response = await api.get('/states/input');
      setAllRows(response.data || []);
    } catch (error) {
      setNotification({ open: true, message: 'Failed to load state inputs', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStateInputs();
  }, []);

  // Filter rows by selected scenario, entity, state, and filing code
  useEffect(() => {
    if (!selectedScenario) {
      setRows([]);
      setLoading(false); // Ensure loading is false if no scenario selected
      return;
    }

    let filteredRows = (allRows || []).filter((row) => row.ScenarioCode === selectedScenario.scenarioCode);

    // Apply entity filter if specific entities are selected
    const entityArr = Array.isArray(selectedEntity) ? selectedEntity : [];
    if (entityArr.length > 0) {
      filteredRows = filteredRows.filter((row) => entityArr.includes(row.EntityCode));
    }

    // Apply state filter
    const stateArr = Array.isArray(selectedState) ? selectedState : [];
    if (stateArr.length > 0) {
      filteredRows = filteredRows.filter((row) => stateArr.includes(row.StateCode));
    }

    // Apply filing code filter
    const filingArr = Array.isArray(selectedFilingCode) ? selectedFilingCode : [];
    if (filingArr.length > 0) {
      filteredRows = filteredRows.filter((row) => filingArr.includes(row.FilingCode));
    }

    setRows(filteredRows);
    setLoading(false); // Ensure loading is false after filtering
  }, [allRows, selectedScenario, selectedEntity, selectedState, selectedFilingCode]);

  // Dialog handlers
  const handleOpenDialog = (row = defaultStateInput) => {
    if (!row || Object.keys(row).length === 0 || row === defaultStateInput) {
      setCurrentRow({ ...defaultStateInput, ScenarioCode: selectedScenario?.scenarioCode || '' });
      setEditMode(false);
    } else {
      setCurrentRow(row);
      setEditMode(true);
    }
    setOpenDialog(true);
  };

  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  const handleRequestClose = () => setConfirmCloseOpen(true);

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentRow(defaultStateInput);
    setEditMode(false);
    setConfirmCloseOpen(false);
  };

  // CRUD actions
  const handleSave = async () => {
    try {
      if (editMode && currentRow.id) {
        await api.put(`/states/input/${currentRow.id}`, currentRow);
        setNotification({ open: true, message: 'State input updated', severity: 'success' });
      } else {
        await api.post('/states/input', currentRow);
        setNotification({ open: true, message: 'State input added', severity: 'success' });
      }
      fetchStateInputs();
      handleCloseDialog();
    } catch (error) {
      setNotification({ open: true, message: 'Failed to save state input', severity: 'error' });
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/states/input/${id}`);
      setNotification({ open: true, message: 'State input deleted', severity: 'success' });
      fetchStateInputs();
    } catch (error) {
      setNotification({ open: true, message: 'Failed to delete state input', severity: 'error' });
    }
  };

  // Import handlers
  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setImportFile(file);
      setImportDialogOpen(true);
    }
    // Reset so the same file can be re-selected
    event.target.value = null;
  };

  /**
   * Import - STRICT VALIDATION:
   * 1. Only EXISTING StateInput records can be updated
   * 2. ScenarioCode + EntityCode + StateCode are used ONLY for matching
   * 3. Those identity fields cannot be changed via import
   * 4. New rows cannot be created via import
   * 5. Only the rate fields in RATE_FIELDS can be updated
   */
  const importFromExcel = async () => {
    if (!importFile) return;

    try {
      setImporting(true);
      setImportProgress(0);
      setImportStats({ total: 0, updated: 0, failed: 0, skipped: 0, notFound: 0 });

      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          if (jsonData.length === 0) {
            throw new Error('The Excel file is empty');
          }

          const fileColumns = Object.keys(jsonData[0] || {});
          const fileColumnsLower = fileColumns.map((col) => col.toLowerCase());

          // Map frontend field -> possible Excel headers (case-insensitive)
          const columnMappings = {
            ScenarioCode: ['scenario code', 'scenariocode'],
            FilingCode: ['filing code', 'filingcode'],
            EntityCode: ['entity code', 'entitycode'],
            StateCode: ['state code', 'statecode', 'state'],
            CurrentApportionmentRate: ['current apportionment rate', 'currentapportionmentrate'],
            BeginningApportionmentRate: ['begin apportionment rate', 'beginning apportionment rate', 'beginningapportionmentrate'],
            EndingApportionmentRate: ['end apportionment rate', 'ending apportionment rate', 'endingapportionmentrate'],
            CurrentStateRate: ['current state rate', 'currentstaterate'],
            BeginningStateRate: ['begin state rate', 'beginning state rate', 'beginningstaterate'],
            EndingStateRate: ['end state rate', 'ending state rate', 'endingstaterate']
          };

          const matchedColumns = {};
          const missingColumns = [];
          for (const [field, possibleNames] of Object.entries(columnMappings)) {
            const found = fileColumnsLower.some((col, index) => {
              if (possibleNames.includes(col.toLowerCase())) {
                matchedColumns[field] = fileColumns[index];
                return true;
              }
              return false;
            });
            // Identity columns are required for matching
            if (!found && ['ScenarioCode', 'FilingCode', 'EntityCode'].includes(field)) {
              missingColumns.push(possibleNames[0]);
            }
          }

          if (missingColumns.length > 0) {
            throw new Error(`Missing required columns: ${missingColumns.join(', ')}`);
          }

          const localStats = {
            total: jsonData.length,
            updated: 0,
            failed: 0,
            skipped: 0,
            notFound: 0
          };
          setImportStats(localStats);

          const errorLog = [];

          // Build lookup map of existing inputs keyed by ScenarioCode|FilingCode|EntityCode
          // (A combined filing can cover multiple member entities, so EntityCode is
          // needed to disambiguate rows that share the same FilingCode.)
          const existingResp = await api.get('/states/input');
          const existingRows = existingResp.data || [];
          const inputsMap = new Map();
          existingRows.forEach((input) => {
            if (!input.FilingCode || !input.EntityCode) return;
            const key = `${input.ScenarioCode}|${input.FilingCode}|${input.EntityCode}`.toLowerCase();
            inputsMap.set(key, input);
          });

          for (let i = 0; i < jsonData.length; i++) {
            const row = jsonData[i];
            const rowNumber = i + 2; // account for header

            try {
              const scenarioCode = String(row[matchedColumns.ScenarioCode] || '').trim();
              const filingCode = String(row[matchedColumns.FilingCode] || '').trim();
              const entityCode = String(row[matchedColumns.EntityCode] || '').trim();

              if (!scenarioCode || !filingCode || !entityCode) {
                errorLog.push(`Row ${rowNumber}: Missing ScenarioCode, FilingCode, or EntityCode`);
                localStats.failed++;
                setImportStats({ ...localStats });
                continue;
              }

              const key = `${scenarioCode}|${filingCode}|${entityCode}`.toLowerCase();
              const existingInput = inputsMap.get(key);

              if (!existingInput) {
                errorLog.push(`Row ${rowNumber}: No existing record for ${scenarioCode}/${filingCode}/${entityCode} - import only updates existing records`);
                localStats.notFound++;
                localStats.skipped++;
                setImportStats({ ...localStats });
                setImportProgress(Math.round((i + 1) / jsonData.length * 100));
                continue;
              }

              // Build update with only rate fields, only if value changed
              const updateData = {};
              let hasChanges = false;

              RATE_FIELDS.forEach((field) => {
                if (matchedColumns[field] && row[matchedColumns[field]] !== undefined) {
                  const value = row[matchedColumns[field]];
                  let cleanValue = value;
                  if (typeof value === 'string' && value.includes('%')) {
                    cleanValue = parseFloat(value.replace('%', '')) / 100;
                  } else if (value !== null && value !== '') {
                    cleanValue = parseFloat(value);
                  }
                  if (cleanValue !== existingInput[field]) {
                    updateData[field] = cleanValue;
                    hasChanges = true;
                  }
                }
              });

              if (hasChanges) {
                // Preserve identity fields explicitly
                await api.put(`/states/input/${existingInput.id}`, {
                  ...existingInput,
                  ...updateData,
                  ScenarioCode: existingInput.ScenarioCode,
                  FilingCode: existingInput.FilingCode,
                  EntityCode: existingInput.EntityCode,
                  StateCode: existingInput.StateCode,
                  ModifiedOn: new Date().toISOString(),
                  modifiedBy: 'admin'
                });
                localStats.updated++;
              } else {
                localStats.skipped++;
              }
            } catch (rowError) {
              console.error(`Error processing row ${i + 1}:`, rowError);
              errorLog.push(`Row ${rowNumber}: ${rowError.message || 'Unknown error'}`);
              localStats.failed++;
            }

            setImportStats({ ...localStats });
            setImportProgress(Math.round((i + 1) / jsonData.length * 100));
          }

          let message = `Import complete: ${localStats.updated} updated, ${localStats.skipped} skipped`;
          if (localStats.notFound > 0) {
            message += `, ${localStats.notFound} not found (cannot create new records via import)`;
          }
          if (localStats.failed > 0) {
            message += `, ${localStats.failed} failed`;
          }

          if (errorLog.length > 0) {

          }

          const severity = localStats.failed > 0 || localStats.notFound > 0 ? 'warning' : 'success';
          setNotification({ open: true, message, severity });
          fetchStateInputs();
        } catch (error) {
          console.error('Import error:', error);
          setNotification({
            open: true,
            message: error.message || 'Failed to import state inputs',
            severity: 'error'
          });
        } finally {
          setImporting(false);
          setImportDialogOpen(false);
          setImportFile(null);
        }
      };

      reader.onerror = () => {
        setNotification({ open: true, message: 'Failed to read file', severity: 'error' });
        setImporting(false);
        setImportDialogOpen(false);
      };

      reader.readAsArrayBuffer(importFile);
    } catch (error) {
      console.error('Import error:', error);
      setNotification({ open: true, message: 'Failed to import state inputs', severity: 'error' });
      setImporting(false);
      setImportDialogOpen(false);
    }
  };



  // Table columns
  const columns = [
  ...STATE_INPUT_COLUMNS,
  {
    field: 'actions',
    headerName: 'Actions',
    width: 100,
    renderCell: (params) =>
    <Box>
          <IconButton size="small" onClick={() => handleOpenDialog(params.row)} title="Edit State Input">
            <img src="/assets/Icons/icon_pencil.svg" alt="Edit" style={{ width: 18, height: 18 }} />
          </IconButton>
        </Box>

  }];


  return (
    <div className="entity-inputs-container">
      <div className="main-content">
        <div className="toolbar-container">
          <Typography className="page-title" variant="h6">State Inputs</Typography>
          <div className="button-group">
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              className="action-button"
              onClick={exportToExcel}
              disabled={rows.length === 0}>

              Export
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<UploadIcon />}
              className="action-button"
              onClick={handleImportClick}>

              Import
            </Button>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept=".xlsx,.xls"
              style={{ display: 'none' }} />

          </div>
        </div>
        <div className="table-container">
          {loading && <LinearProgress />}
          <SearchableDataTable
            searchPlaceholder="Search State Inputs..."
            rows={rows}
            columns={columns}
            loading={loading}
            tableName="stateInputs"
            checkboxSelection
            selectionModel={selectedRows}
            onSelectionModelChange={setSelectedRows}
            height="100%"
            className="custom-datagrid" />

        </div>
      </div>
      {/* Dialog for Add/Edit */}
      <UnsavedChangesDialog
        open={confirmCloseOpen}
        onConfirm={handleCloseDialog}
        onCancel={() => setConfirmCloseOpen(false)} />

      <Dialog open={openDialog} onClose={handleRequestClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode ? 'Edit State Input' : 'Add State Input'}</DialogTitle>
        <DialogContent>
          {STATE_INPUT_COLUMNS.map((col) => {
            if (col.field === 'EntityCode') {
              return (
                <Autocomplete
                  key="EntityCode"
                  options={masterEntities.map((e) => e.EntityCode).sort((a, b) => a.localeCompare(b))}
                  loading={entityLoading}
                  value={currentRow.EntityCode || ''}
                  onChange={(e, newValue) => {
                    setCurrentRow({
                      ...currentRow,
                      EntityCode: newValue || '',
                      EntityName: masterEntities.find((ent) => ent.EntityCode === newValue)?.EntityName || ''
                    });
                  }}
                  renderInput={(params) =>
                  <TextField {...params} margin="dense" label={col.headerName} fullWidth required autoComplete="off" />
                  } />);


            }
            if (col.field === 'StateCode') {
              return (
                <Autocomplete
                  key="StateCode"
                  options={masterStates.map((s) => s.StateCode).sort((a, b) => a.localeCompare(b))}
                  loading={stateLoading}
                  value={currentRow.StateCode || ''}
                  onChange={(e, newValue) => {
                    setCurrentRow({
                      ...currentRow,
                      StateCode: newValue || '',
                      StateName: masterStates.find((st) => st.StateCode === newValue)?.StateName || ''
                    });
                  }}
                  renderInput={(params) =>
                  <TextField {...params} margin="dense" label={col.headerName} fullWidth required autoComplete="off" />
                  } />);


            }
            const isNumeric = col.type === 'number' || ['CurrentApportionmentRate', 'BeginningApportionmentRate', 'EndingApportionmentRate', 'CurrentStateRate', 'BeginningStateRate', 'EndingStateRate'].includes(col.field);
            return (
              <TextField
                key={col.field}
                margin="dense"
                label={col.headerName}
                fullWidth
                autoComplete="off"
                value={currentRow[col.field] ?? ''}
                onChange={(e) => {
                  const val = e.target.value;
                  setCurrentRow({ ...currentRow, [col.field]: isNumeric && val === '' ? null : val });
                }}
                disabled={col.field === 'id'} />);


          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onClose={() => !importing && setImportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Import State Inputs</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              <strong>Important:</strong> Import only updates rate fields for existing records.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              • Rows must match existing ScenarioCode + FilingCode + EntityCode combinations<br />
              • Only rate fields can be updated (apportionment rates, state rates)<br />
              • ScenarioCode, FilingCode, EntityCode, and StateCode cannot be changed<br />
              • New rows cannot be added via import
            </Typography>
            {importFile &&
            <Typography variant="body2" sx={{ mb: 2 }}>
                Selected file: <strong>{importFile.name}</strong>
              </Typography>
            }
            {importing &&
            <Box sx={{ mt: 2 }}>
                <LinearProgress variant="determinate" value={importProgress} />
                <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                  {importProgress}% - Processing {importStats.total} rows...
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                  Updated: {importStats.updated} | Skipped: {importStats.skipped} | Failed: {importStats.failed}
                </Typography>
              </Box>
            }
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)} disabled={importing}>
            Cancel
          </Button>
          <Button onClick={importFromExcel} variant="contained" disabled={!importFile || importing}>
            {importing ? 'Importing...' : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar open={notification.open} autoHideDuration={4000} onClose={() => setNotification({ ...notification, open: false })}>
        <Alert onClose={() => setNotification({ ...notification, open: false })} severity={notification.severity} sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </div>);

};

export default StateInputs;
